import { X } from 'lucide-react';
import { Avatar as RA, Progress as RP } from 'radix-ui';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import './display.css';

export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

/* ---------- Badge / Pill ---------- */

export interface BadgeProps {
  tone?: Tone;
  /** `soft` (default) tinted fill; `solid` for counts on nav items; `outline` for low emphasis. */
  variant?: 'soft' | 'solid' | 'outline';
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  title?: string;
}

export function Badge({
  tone = 'neutral',
  variant = 'soft',
  icon,
  children,
  className,
  title,
}: BadgeProps) {
  return (
    <span
      className={cx('vel-badge', className)}
      data-tone={tone}
      data-variant={variant}
      title={title}
    >
      {icon ? (
        <span className="vel-badge__icon" aria-hidden>
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/* ---------- StatusChip ---------- */

export interface StatusChipProps {
  tone: Tone;
  label: ReactNode;
  /** Pulsing dot for live states (e.g. "In chair"); off under reduced motion. */
  live?: boolean;
  className?: string;
}

/** Dot + label. One status per row (Stripe rule); colour is never the only signal. */
export function StatusChip({ tone, label, live = false, className }: StatusChipProps) {
  return (
    <span className={cx('vel-status', className)} data-tone={tone}>
      <span className="vel-status__dot" data-live={live ? '' : undefined} aria-hidden />
      {label}
    </span>
  );
}

/* ---------- Chip (filter chip) ---------- */

export interface ChipProps {
  children: ReactNode;
  /** Shows a remove button with this accessible label, e.g. "Remove provider filter". */
  onRemove?: () => void;
  removeLabel?: string;
  selected?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
  className?: string;
}

export function Chip({
  children,
  onRemove,
  removeLabel = 'Remove',
  selected = false,
  onClick,
  icon,
  className,
}: ChipProps) {
  const content = (
    <>
      {icon ? (
        <span className="vel-chip__icon" aria-hidden>
          {icon}
        </span>
      ) : null}
      <span className="vel-chip__label">{children}</span>
    </>
  );
  return (
    <span className={cx('vel-chip', className)} data-selected={selected ? '' : undefined}>
      {onClick ? (
        <button type="button" className="vel-chip__main" aria-pressed={selected} onClick={onClick}>
          {content}
        </button>
      ) : (
        <span className="vel-chip__main">{content}</span>
      )}
      {onRemove ? (
        <button
          type="button"
          className="vel-chip__remove"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <X size={12} aria-hidden />
        </button>
      ) : null}
    </span>
  );
}

/* ---------- Avatar ---------- */

const AVATAR_RAMPS = ['navy', 'orange', 'green', 'blue', 'violet', 'red', 'amber'] as const;

/** Stable ramp choice from a name, so a person keeps their colour everywhere. */
export function avatarRamp(name: string): (typeof AVATAR_RAMPS)[number] {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_RAMPS[h % AVATAR_RAMPS.length] as (typeof AVATAR_RAMPS)[number];
}

/** "Ana María Ruiz" -> "AR"; "Kim" -> "K". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export interface AvatarProps {
  name: string;
  src?: string;
  size?: number;
  /** Privacy mode shows initials only, never the photo. */
  privacy?: boolean;
  className?: string;
}

/** "Ana Mar�a Ruiz" -> "A. M. R." Same rule as the shell's privacy `maskName()`. */
function maskedLabel(name: string): string {
  const parts = name.replace(/[(),]/g, ' ').trim().split(/\s+/).filter(Boolean);
  return parts.map((p) => `${p[0]?.toUpperCase() ?? ''}.`).join(' ');
}

export function Avatar({ name, src, size = 28, privacy = false, className }: AvatarProps) {
  // The colour stays keyed on the real name so it does not jump when privacy toggles;
  // in privacy mode the accessible label carries initials only, never the full name.
  const ramp = avatarRamp(name);
  const style = {
    width: size,
    height: size,
    fontSize: Math.max(10, Math.round(size * 0.38)),
    '--avatar-bg': `var(--${ramp}-4)`,
    '--avatar-fg': `var(--${ramp}-12)`,
  } as CSSProperties;
  return (
    <RA.Root className={cx('vel-avatar', className)} style={style}>
      {src && !privacy ? <RA.Image className="vel-avatar__img" src={src} alt={name} /> : null}
      <RA.Fallback className="vel-avatar__fallback" {...(src && !privacy ? { delayMs: 300 } : {})}>
        <span aria-hidden>{initials(name)}</span>
        <span className="vel-visually-hidden">{privacy ? maskedLabel(name) : name}</span>
      </RA.Fallback>
    </RA.Root>
  );
}

/* ---------- Skeleton ---------- */

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: 'sm' | 'md' | 'lg' | 'pill';
  className?: string;
}

/** Loading placeholder. Only for first load: data refresh never shows skeletons or animates. */
export function Skeleton({ width = '100%', height = 12, radius = 'sm', className }: SkeletonProps) {
  return (
    <span
      className={cx('vel-skeleton', className)}
      style={{ width, height, borderRadius: `var(--radius-${radius})` }}
      aria-hidden
    />
  );
}

/* ---------- ProgressBar / Meter ---------- */

export interface ProgressBarProps {
  /** 0..max, or null for indeterminate. */
  value: number | null;
  max?: number;
  label: string;
  tone?: Tone;
  className?: string;
}

export function ProgressBar({
  value,
  max = 100,
  label,
  tone = 'brand',
  className,
}: ProgressBarProps) {
  const pct = value === null ? null : Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <RP.Root
      className={cx('vel-progress', className)}
      value={value}
      max={max}
      aria-label={label}
      data-tone={tone}
    >
      <RP.Indicator
        className="vel-progress__bar"
        data-indeterminate={pct === null ? '' : undefined}
        style={pct === null ? undefined : { width: `${pct}%` }}
      />
    </RP.Root>
  );
}

export interface MeterProps {
  value: number;
  max: number;
  label: string;
  /** Formats the visible value, e.g. (v, m) => `${v} of ${m} seats`. */
  format?: (value: number, max: number) => string;
  /** Fractions at which the bar turns warning / danger. */
  thresholds?: { warning: number; danger: number };
  className?: string;
}

/** Usage meter (seats, SMS credits, storage). Health shown by tone and text. */
export function Meter({
  value,
  max,
  label,
  format = (v, m) => `${v.toLocaleString()} / ${m.toLocaleString()}`,
  thresholds = { warning: 0.8, danger: 0.95 },
  className,
}: MeterProps) {
  const ratio = max > 0 ? value / max : 0;
  const tone: Tone =
    ratio >= thresholds.danger ? 'danger' : ratio >= thresholds.warning ? 'warning' : 'brand';
  const text = format(value, max);
  return (
    <div className={cx('vel-meter', className)}>
      <div className="vel-meter__head">
        <span className="vel-meter__label">{label}</span>
        <span className="vel-meter__value vel-num">{text}</span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={text}
        className="vel-progress"
        data-tone={tone}
      >
        <div className="vel-progress__bar" style={{ width: `${Math.min(100, ratio * 100)}%` }} />
      </div>
    </div>
  );
}

/* ---------- Card ---------- */

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  /** Small muted text beside the title. */
  meta?: ReactNode;
  actions?: ReactNode;
  /** Remove body padding (for tables and lists that run edge to edge). */
  flush?: boolean;
  /** Raised cards get the second elevation step (popover-like panels). */
  raised?: boolean;
}

export function Card({
  title,
  meta,
  actions,
  flush,
  raised,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <section
      className={cx('vel-card', className)}
      data-flush={flush ? '' : undefined}
      data-raised={raised ? '' : undefined}
      {...rest}
    >
      {title || actions ? (
        <header className="vel-card__header">
          <div className="vel-card__titles">
            {title ? <h3 className="vel-card__title">{title}</h3> : null}
            {meta ? <span className="vel-card__meta">{meta}</span> : null}
          </div>
          {actions ? <div className="vel-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="vel-card__body">{children}</div>
    </section>
  );
}
